import React, { useCallback, useMemo, useState } from 'react';

import CustomOffcanvas, { type CustomOffcanvasProps } from '../CustomOffcanvas';
import CustomTable, {
  CustomTableHeader,
  CustomTableProps,
} from '../CustomTable';
import DetailsTable from '../CustomTable/DetailsTable';
import { TableDetailsRow } from '../CustomTable/types/table.type';

type OffcanvasContent<T> =
  | React.ReactNode
  | ((row: T | null) => React.ReactNode);

export interface CustomTableOffcanvasProps<T extends Record<string, unknown>>
  extends Pick<
      CustomTableProps<T>,
      | 'headers'
      | 'data'
      | 'itemsPerPage'
      | 'showPagination'
      | 'emptyMessage'
      | 'filterable'
      | 'filterPlaceholder'
      | 'sortable'
      | 'minHeight'
      | 'data-testid'
    >,
    Pick<
      CustomOffcanvasProps,
      | 'footerContent'
      | 'openRoute'
      | 'showOpenButton'
      | 'style'
      | 'placement'
      | 'titleId'
      | 'closeButtonAriaLabel'
      | 'autoFocusTitle'
    > {
  displayValues?: Record<string, string>;
  offcanvasTitle: string;
  onOpenOffcanvas?: (row: T, index: number) => void;
  onRowClick?: (row: T, index: number) => void;
  widthOffcanvas?: CustomOffcanvasProps['width'];
  children?: OffcanvasContent<T>;
  'data-testid'?: string;
  'data-testid-off-canvas'?: string;
}

const normalizeDetailsRow = <T extends Record<string, unknown>>(
  row: T | null,
): TableDetailsRow | null => {
  if (!row) return null;

  const rowId = (row as { id?: string | number }).id ?? 'detalhe';

  return { id: rowId, ...(row as Record<string, React.ReactNode>) };
};

const CustomTableOffcanvas = <T extends Record<string, unknown>>({
  headers,
  data,
  displayValues,
  offcanvasTitle,
  onOpenOffcanvas,
  onRowClick,
  widthOffcanvas,
  showPagination = true,
  itemsPerPage = 20,
  emptyMessage,
  footerContent,
  children,
  filterable,
  filterPlaceholder,
  sortable,
  minHeight,
  openRoute,
  showOpenButton,
  style,
  placement,
  titleId,
  closeButtonAriaLabel,
  autoFocusTitle,
  'data-testid': dataTestId,
}: CustomTableOffcanvasProps<T>) => {
  const [selectedRow, setSelectedRow] = useState<T | null>(null);
  const [showOffcanvas, setShowOffcanvas] = useState(false);

  const handleOpenOffcanvas = useCallback(
    (row: T, index: number) => {
      setSelectedRow(row);
      setShowOffcanvas(true);
      onOpenOffcanvas?.(row, index);
    },
    [onOpenOffcanvas],
  );

  const handleCloseOffcanvas = useCallback(() => {
    setShowOffcanvas(false);
    setSelectedRow(null);
  }, []);

  const defaultDetailsRow = useMemo(
    () => normalizeDetailsRow(selectedRow),
    [selectedRow],
  );

  const offcanvasContent = useMemo(() => {
    if (typeof children === 'function') {
      return children(selectedRow);
    }

    if (children) {
      return children;
    }

    return (
      <DetailsTable row={defaultDetailsRow} displayValues={displayValues} />
    );
  }, [children, defaultDetailsRow, displayValues, selectedRow]);

  return (
    <>
      <CustomTable
        headers={headers as CustomTableHeader<T>[]}
        data={data}
        onRowClick={
          onRowClick ? (row, index) => onRowClick(row, index) : undefined
        }
        showIconOffcanvas
        onClickOffCanvas={handleOpenOffcanvas}
        itemsPerPage={itemsPerPage}
        showPagination={showPagination}
        emptyMessage={emptyMessage}
        filterable={filterable}
        filterPlaceholder={filterPlaceholder}
        sortable={sortable}
        minHeight={minHeight}
        data-testid={dataTestId}
      />

      <CustomOffcanvas
        show={showOffcanvas}
        onClose={handleCloseOffcanvas}
        title={offcanvasTitle}
        width={widthOffcanvas}
        footerContent={footerContent}
        openRoute={openRoute}
        showOpenButton={showOpenButton}
        style={style}
        placement={placement}
        titleId={titleId}
        closeButtonAriaLabel={closeButtonAriaLabel}
        autoFocusTitle={autoFocusTitle}
        data-testid={`${dataTestId}-off-canvas`}
      >
        {offcanvasContent}
      </CustomOffcanvas>
    </>
  );
};

export default CustomTableOffcanvas;
